/**
 * Calendar days are plain "YYYY-MM-DD" strings everywhere (API, DB @db.Date, UI). A habit
 * at 07:00 belongs to the user's local day, not a UTC instant, so day math never goes
 * through Date objects in the local zone — only through UTC midnight, where it's exact.
 */

export type IsoDate = string;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function toUtc(date: IsoDate): Date {
  return new Date(`${date}T00:00:00Z`);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = toUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday, matching Date#getDay. */
export function weekdayOf(date: IsoDate): number {
  return toUtc(date).getUTCDay();
}

/** Monday of the week containing `date` (weeks start on Monday in es-*). */
export function startOfWeek(date: IsoDate): IsoDate {
  const offset = (weekdayOf(date) + 6) % 7;
  return addDays(date, -offset);
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / 86_400_000);
}

export interface LocalMoment {
  date: IsoDate;
  /** "HH:mm", 24h. */
  time: string;
  /** Minutes since local midnight. */
  minutes: number;
}

/** The wall-clock date and time `now` corresponds to in an IANA time zone. */
export function localMoment(now: Date, timeZone: string): LocalMoment {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "00";
  const time = `${get("hour")}:${get("minute")}`;
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time,
    minutes: timeToMinutes(time),
  };
}

export function timeToMinutes(time: string): number {
  const [h = "0", m = "0"] = time.split(":");
  return Number(h) * 60 + Number(m);
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}
